import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { formatDateDisplay } from "../../lib/utils";
import { Plus, Pencil, Trash2, X, Upload, Image, Sparkles } from "lucide-react";
import { toast } from "sonner";

const TAG_LABELS = { news: "News", release: "Release", sale: "Sale / Deal", discussion: "Discussion" };
const TAG_STYLES = {
  news:       "bg-sky-500/20 text-sky-300",
  release:    "bg-violet-500/20 text-violet-300",
  sale:       "bg-emerald-500/20 text-emerald-300",
  discussion: "bg-gold/20 text-gold",
};

const empty = { title: "", body: "", tag: "news", image: "", ai_context: "", show_on_home: false, home_position: "" };

export default function AdminPosts() {
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);

  const load = async () => {
    const r = await api.get("/posts", { params: { limit: 200 } });
    setPosts(r.data);
  };

  useEffect(() => { load(); }, []);

  const onSave = async () => {
    if (!editing.title.trim() || !editing.body.trim()) {
      toast.error("Title and body are required.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: editing.title,
        body: editing.body,
        tag: editing.tag,
        image: editing.image || null,
        show_on_home: editing.show_on_home || false,
        home_position: editing.show_on_home && editing.home_position !== "" ? Number(editing.home_position) : null,
      };
      if (editing.id) {
        await api.put(`/admin/posts/${editing.id}`, payload);
        toast.success("Post updated");
      } else {
        await api.post("/admin/posts", payload);
        toast.success("Post published");
      }
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const onGenerateAI = async () => {
    if (!editing.title.trim()) return toast.error("Enter a title first");
    setGenerating(true);
    try {
      const r = await api.post("/admin/posts/generate-ai", { title: editing.title, tag: editing.tag, context: editing.ai_context || "" });
      setEditing((p) => ({ ...p, body: r.data.body || p.body }));
      toast.success("AI content generated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI generation failed", { duration: 5000 });
    } finally {
      setGenerating(false);
    }
  };

  const onDelete = async (post) => {
    if (!window.confirm(`Delete "${post.title}"?`)) return;
    await api.delete(`/admin/posts/${post.id}`);
    toast.success("Post deleted");
    await load();
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = r.data.url;
      setEditing((prev) => ({ ...prev, image: url }));
      toast.success("Image uploaded");
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-gold mb-2">Community</div>
          <h1 className="text-3xl font-black uppercase tracking-tighter">Posts</h1>
        </div>
        <button
          onClick={() => setEditing({ ...empty })}
          className="bg-gold text-navy-900 font-extrabold uppercase tracking-wider text-xs px-5 py-2.5 rounded-full hover:bg-gold-light flex items-center gap-2"
        >
          <Plus className="w-4 h-4" /> New Post
        </button>
      </div>

      <div className="bg-navy-700 rounded-xl neon-border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-navy-800 text-[10px] uppercase tracking-[0.2em] text-gold/80">
            <tr>
              <th className="text-left p-4">Post</th>
              <th className="text-left p-4">Tag</th>
              <th className="text-left p-4">Home</th>
              <th className="text-right p-4">Likes</th>
              <th className="text-left p-4">Date</th>
              <th className="p-4"></th>
            </tr>
          </thead>
          <tbody>
            {posts.map((post) => (
              <tr key={post.id} className="border-t border-gold/10 hover:bg-navy-800/50">
                <td className="p-4">
                  <div className="flex items-center gap-3">
                    {post.image ? (
                      <img src={post.image} alt="" className="w-12 h-10 object-cover rounded shrink-0" />
                    ) : (
                      <div className="w-12 h-10 rounded bg-navy-700 flex items-center justify-center shrink-0">
                        <Image className="w-4 h-4 text-slate-600" />
                      </div>
                    )}
                    <div className="min-w-0">
                      <div className="font-semibold truncate max-w-xs">{post.title}</div>
                      <div className="text-xs text-slate-400 truncate max-w-xs">{post.body}</div>
                    </div>
                  </div>
                </td>
                <td className="p-4">
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded font-bold ${TAG_STYLES[post.tag] || ""}`}>
                    {TAG_LABELS[post.tag] || post.tag}
                  </span>
                </td>
                <td className="p-4">
                  {post.show_on_home ? (
                    <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded font-bold bg-gold/20 text-gold">
                      #{post.home_position ?? "—"}
                    </span>
                  ) : (
                    <span className="text-slate-600 text-xs">—</span>
                  )}
                </td>
                <td className="p-4 text-right">{post.likes}</td>
                <td className="p-4 text-xs text-slate-400">{formatDateDisplay(post.created_at)}</td>
                <td className="p-4 text-right">
                  <div className="flex justify-end gap-2">
                    <button onClick={() => setEditing({ ...post })} className="p-1.5 text-slate-300 hover:text-gold"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => onDelete(post)} className="p-1.5 text-slate-300 hover:text-rose-400"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {posts.length === 0 && (
              <tr><td colSpan={6} className="p-8 text-center text-slate-400">No posts yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-navy-900/80 backdrop-blur flex items-center justify-center p-4">
          <div className="bg-navy-800 rounded-2xl p-6 max-w-2xl w-full neon-border max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-xl font-black uppercase tracking-tight">{editing.id ? "Edit" : "New"} Post</h2>
              <button onClick={() => setEditing(null)} className="p-1 text-slate-400 hover:text-gold"><X className="w-5 h-5" /></button>
            </div>

            <div className="space-y-4">
              {/* Cover image */}
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 mb-2">Cover Image</div>
                {editing.image && (
                  <div className="relative mb-2 rounded-xl overflow-hidden h-44">
                    <img src={editing.image} alt="" className="w-full h-full object-cover" />
                    <button
                      onClick={() => setEditing((p) => ({ ...p, image: "" }))}
                      className="absolute top-2 right-2 bg-rose-500 text-white rounded-full p-1"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                )}
                <div className="flex gap-2">
                  <label className="flex-1 cursor-pointer flex items-center gap-2 bg-navy-700 border border-gold/20 rounded-md px-3 py-2 text-xs hover:border-gold/50">
                    <Upload className="w-4 h-4 text-gold" />
                    {uploading ? "Uploading…" : "Upload image"}
                    <input type="file" accept="image/*" onChange={onUpload} className="hidden" />
                  </label>
                  <input
                    placeholder="or paste image URL + Enter"
                    disabled={uploading}
                    className="flex-1 bg-navy-700 border border-gold/20 rounded-md px-3 py-2 text-xs focus:outline-none focus:border-gold/60 disabled:opacity-50"
                    onKeyDown={async (e) => {
                      const val = e.currentTarget.value.trim();
                      if (e.key !== "Enter" || !val) return;
                      e.currentTarget.value = "";
                      setUploading(true);
                      try {
                        const r = await api.post("/admin/upload-from-url", { url: val });
                        setEditing((p) => ({ ...p, image: r.data.url }));
                        toast.success("Image saved to CDN");
                      } catch (err) {
                        toast.error(err?.response?.data?.detail || "Could not fetch that URL");
                      } finally {
                        setUploading(false);
                      }
                    }}
                  />
                </div>
              </div>

              {/* Title */}
              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.2em] text-slate-400">Title</span>
                <input
                  value={editing.title}
                  onChange={(e) => setEditing((p) => ({ ...p, title: e.target.value }))}
                  maxLength={120}
                  className="w-full mt-1 bg-navy-700 border border-gold/20 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gold/60"
                />
              </label>

              {/* AI context */}
              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.2em] text-slate-400">Additional context for AI <span className="normal-case tracking-normal text-slate-500">(optional — price, date, pre-order link, discount code, goodies, etc.)</span></span>
                <textarea
                  value={editing.ai_context}
                  onChange={(e) => setEditing((p) => ({ ...p, ai_context: e.target.value }))}
                  rows={3}
                  placeholder="e.g. Pre-order link: gamestrade.in/preorder, Price: ₹5,499, Launch: 5 June 2025, Free DualSense skin on pre-order, Use code GT10 for 10% off"
                  className="w-full mt-1 bg-navy-700 border border-gold/20 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gold/60 resize-none text-slate-300 placeholder:text-slate-600"
                />
              </label>

              {/* Body */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] uppercase tracking-[0.2em] text-slate-400">Body</span>
                  <button
                    type="button"
                    onClick={onGenerateAI}
                    disabled={generating}
                    className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold px-3 py-1 rounded-full border border-gold/30 text-gold hover:bg-gold/10 disabled:opacity-50 transition"
                  >
                    <Sparkles className="w-3 h-3" />
                    {generating ? "Generating…" : "Generate with AI"}
                  </button>
                </div>
                <textarea
                  value={editing.body}
                  onChange={(e) => setEditing((p) => ({ ...p, body: e.target.value }))}
                  rows={12}
                  maxLength={4000}
                  placeholder={"Supports markdown:\n## Heading\n**bold text**\n- bullet item"}
                  className="w-full bg-navy-700 border border-gold/20 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gold/60 resize-y font-mono"
                />
              </div>

              {/* Tag */}
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 mb-2">Tag</div>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(TAG_LABELS).map(([k, label]) => (
                    <button
                      key={k}
                      type="button"
                      onClick={() => setEditing((p) => ({ ...p, tag: k }))}
                      className={`text-xs uppercase tracking-wider px-3 py-1.5 rounded-full border transition font-bold ${
                        editing.tag === k
                          ? TAG_STYLES[k] + " border-current"
                          : "border-gold/20 text-slate-400 hover:border-gold/40"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Home showcase */}
              <div className="border border-gold/15 rounded-xl p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-bold text-slate-200">Show on Home Page</div>
                    <div className="text-[11px] text-slate-500 mt-0.5">Feature this post in the home carousel</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setEditing((p) => ({ ...p, show_on_home: !p.show_on_home, home_position: p.show_on_home ? "" : p.home_position }))}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${editing.show_on_home ? "bg-gold" : "bg-navy-600"}`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${editing.show_on_home ? "translate-x-6" : "translate-x-1"}`} />
                  </button>
                </div>
                {editing.show_on_home && (
                  <label className="block">
                    <span className="text-[10px] uppercase tracking-[0.2em] text-slate-400">Position <span className="normal-case tracking-normal text-slate-500">(1 = first in carousel)</span></span>
                    <input
                      type="number"
                      min={1}
                      value={editing.home_position}
                      onChange={(e) => setEditing((p) => ({ ...p, home_position: e.target.value }))}
                      placeholder="e.g. 1"
                      className="w-full mt-1 bg-navy-700 border border-gold/20 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gold/60"
                    />
                  </label>
                )}
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button onClick={() => setEditing(null)} className="flex-1 border border-gold/30 text-slate-300 font-bold uppercase tracking-wider text-xs py-3 rounded-full hover:bg-navy-700">
                Cancel
              </button>
              <button
                onClick={onSave}
                disabled={saving || !editing.title.trim()}
                className="flex-1 bg-gold text-navy-900 font-extrabold uppercase tracking-wider text-xs py-3 rounded-full hover:bg-gold-light disabled:opacity-50"
              >
                {saving ? "Saving…" : editing.id ? "Update Post" : "Publish Post"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
